/**
 * Pure helpers for Contract Intelligence: formatting, and reading an
 * bundle's versions. Nothing here calculates a value the documents do not
 * state; it only finds, labels and formats what the backend sent.
 */
import type {
    Cell,
    ContractDetail,
    ContractDocument,
    ContractOpportunity,
    ContractVersion,
    Gap,
    OpportunityCount,
    OpportunityKind,
} from "./contractExplorerTypes.ts";
import {KIND_CATEGORY} from "./contractExplorerTypes.ts";

export const MISSING = '—';

export const formatDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) return MISSING;
    const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return MISSING;
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
};

/** `1278.5, 'EUR'` → `€1.3K`; zero or missing → a dash. */
export const formatMoney = (value: number | null | undefined, currency?: string): string => {
    if (value === null || value === undefined || !(Math.abs(value) > 0)) return MISSING;
    const options: Intl.NumberFormatOptions = {notation: 'compact', maximumFractionDigits: 1};
    if (currency && /^[A-Z]{3}$/.test(currency)) {
        return new Intl.NumberFormat('en-GB', {...options, style: 'currency', currency}).format(value);
    }
    const text = new Intl.NumberFormat('en-GB', options).format(value);
    return currency ? `${text} ${currency}` : text;
};

/** A number as the document states it, grouped, without rounding it away. */
export const formatNumber = (value: unknown): string =>
    typeof value === 'number' ? new Intl.NumberFormat('en-GB', {maximumFractionDigits: 6}).format(value) : MISSING;

/** A spend column as cat-material names it (`part__p_mat_name`) → the column a databag's config labels (`p_mat_name`). */
export const spendColumnField = (name: string): string => name.split('__').at(-1) ?? name;

/** `master_agreement` → `Master agreement`. */
export function typeLabel(value: string | null | undefined): string {
    if (!value) return MISSING;
    const words = value.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

const DURATION_UNITS: Record<string, string> = {Y: 'year', M: 'month', W: 'week', D: 'day'};

/** An ISO 8601 duration as the schema stores it (`P3Y`, `P90D`) in words; anything else as it is. */
export function formatDuration(value: unknown): string {
    if (typeof value !== 'string' || !value) return MISSING;
    const match = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/.exec(value);
    if (!match || !match.slice(1).some(Boolean)) return value;
    return ['Y', 'M', 'W', 'D']
        .map((unit, i) => [Number(match[i + 1]), DURATION_UNITS[unit]] as const)
        .filter(([n]) => n > 0)
        .map(([n, word]) => `${n} ${word}${n === 1 ? '' : 's'}`)
        .join(' ');
}

/** Any extracted value as text: lists joined, booleans as yes/no, snake_case enums labelled. */
export function formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return MISSING;
    if (Array.isArray(value)) return value.length ? value.map(formatValue).join(' · ') : MISSING;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return formatNumber(value);
    if (typeof value === 'string' && /^[a-z]+(_[a-z]+)+$/.test(value)) return typeLabel(value);
    return String(value);
}

/** Days from the reference date to an end date; null when either is missing. */
export function daysUntil(end: string | null, asOf: string): number | null {
    if (!end || !asOf) return null;
    const days = Math.round((Date.parse(end.slice(0, 10)) - Date.parse(asOf.slice(0, 10))) / 86_400_000);
    return Number.isFinite(days) ? days : null;
}

/** What a link points at: a line, or the bundle as a whole. */
export const lineLabel = (line: string): string => line || 'The bundle as a whole';

/** Action gaps first, each group in the backend's order. */
export const sortGaps = (gaps: Gap[]): Gap[] =>
    [...gaps.filter(g => g.severity === 'action'), ...gaps.filter(g => g.severity === 'watch')];

/** The header's cells of a version: the item whose only line is `''`. */
export function headerCells(version: ContractVersion | undefined): Record<string, Cell> {
    if (!version) return {};
    for (const lines of Object.values(version.values)) {
        if ('' in lines) return lines[''];
    }
    return {};
}

export type VersionLine = {line: string; cells: Record<string, Cell>};

/** A version's price lines (every item's lines but the header's), in line order. */
export function versionLines(version: ContractVersion | undefined): VersionLine[] {
    if (!version) return [];
    const lines = Object.values(version.values)
        .flatMap(item => Object.entries(item).filter(([line]) => line !== ''))
        .map(([line, cells]) => ({line, cells}));
    const number = (line: string) => Number(line.replace(/\D/g, '')) || 0;
    return lines.sort((a, b) => number(a.line) - number(b.line));
}

/** A line's item, as the latest version that holds it names it. */
export function lineName(versions: ContractVersion[], line: string): string {
    if (!line) return lineLabel(line);
    for (const version of [...versions].reverse()) {
        const cells = versionLines(version).find(l => l.line === line)?.cells;
        const name = cells?.item_description?.value ?? cells?.item_code?.value;
        if (name !== undefined && name !== null && name !== '') return String(name);
    }
    return line;
}

/** document_id → the document, for citations. */
export const documentsById = (documents: ContractDocument[]): Record<string, ContractDocument> =>
    Object.fromEntries(documents.map(d => [d.document_id, d]));

/** The document version 0 starts from: the bundle's base. */
export function baseDocument(contract: ContractDetail): ContractDocument | undefined {
    const first = contract.versions[0];
    return contract.documents.find(d => d.document_id === first?.from_document) ?? contract.documents[0];
}

export type Fact = {value: unknown; documentId: string; change?: Cell['change']; previous?: unknown};

/**
 * A term of the bundle in one version: the version's own cell when it
 * carries the column, else the base document's header value. Undefined when
 * neither states it.
 */
export function termOf(contract: ContractDetail, version: ContractVersion | undefined, field: string): Fact | undefined {
    const cell = headerCells(version)[field];
    if (cell && cell.value !== null && cell.value !== undefined && cell.value !== '') {
        return {value: cell.value, documentId: cell.document_id, change: cell.change, previous: cell.previous};
    }
    const base = baseDocument(contract);
    const value = base?.header?.[field];
    if (value === null || value === undefined || value === '') return undefined;
    return {value, documentId: base!.document_id};
}

const filled = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

/**
 * A line's price as the document states it: the unit price with the period
 * it covers (`1,200 / year`), else its rate or discount percentage. `cell` is
 * the cell it came from, so a change can be marked on it.
 */
export function priceLabel(cells: Record<string, Cell>): {text: string; cell?: Cell} {
    const price = cells.unit_price;
    if (filled(price?.value)) {
        const period = cells.price_period?.value;
        return {text: `${formatNumber(price.value)}${filled(period) ? ` / ${String(period)}` : ''}`, cell: price};
    }
    if (filled(cells.rate_pct?.value)) return {text: `${formatNumber(cells.rate_pct.value)} %`, cell: cells.rate_pct};
    if (filled(cells.discount_pct?.value)) {
        return {text: `${formatNumber(cells.discount_pct.value)} % discount`, cell: cells.discount_pct};
    }
    return {text: MISSING};
}

const BILLING: Record<string, string> = {
    one_time: 'once', weekly: 'weekly', monthly: 'monthly', quarterly: 'quarterly',
    semi_annual: 'twice a year', annual: 'yearly', per_use: 'per use',
};
const TIMING: Record<string, string> = {in_advance: 'in advance', in_arrears: 'in arrears'};

/** How a line is invoiced, when the document says: `billed monthly, in advance`; '' when it does not. */
export function billingLabel(cells: Record<string, Cell>): string {
    const frequency = cells.billing_frequency?.value;
    const timing = cells.billing_timing?.value;
    const words = [filled(frequency) ? BILLING[String(frequency)] ?? String(frequency) : '',
        filled(timing) ? TIMING[String(timing)] ?? String(timing) : ''].filter(Boolean);
    return words.length ? `billed ${words.join(', ')}` : '';
}

/** What a line is, when it is not a plain charge; a charge has no tag. */
export const LINE_TYPE_LABEL: Record<string, string> = {
    discount: 'Discount', free_of_charge: 'Free of charge', in_kind: 'In kind', conditional: 'Conditional',
};

/** A document's own problems, as the extraction flagged them. */
export const ISSUE_LABEL: Record<string, string> = {
    incomplete: 'Incomplete', redacted: 'Redacted', template: 'Template', missing_annex: 'Missing annex',
    multiple_contracts: 'Several contracts', duplicate_pages: 'Duplicate pages', rotated: 'Rotated pages',
    other: 'Other issue',
};

export const SIGNATURE_LABEL: Record<string, string> = {
    all_parties: 'Signed by all parties', some_parties: 'Signed by some parties', none: 'Unsigned',
};

/** The tables of a document that no version holds, by name as the export carries them. */
export const PRICE_TIERS = 'contract_price_tier';
export const OTHER_AMOUNTS = 'contract_other_amount';

export type DocumentRow = {row: Record<string, unknown>; document: ContractDocument};

/** Every document's rows of one table, each with the document it comes from, in document order. */
export const documentRows = (documents: ContractDocument[], table: string): DocumentRow[] =>
    documents.flatMap(document => (document.tables?.[table] ?? []).map(row => ({row, document})));

/**
 * A version line's price tiers. A line's cells name the extracted row they
 * come from (`source_row`, `<document_id>:<line_no>`), and a tier names its
 * document and the line it bands (`item_line_no`), so the two meet there.
 */
export function tiersOf(cells: Record<string, Cell>, documents: ContractDocument[]): DocumentRow[] {
    const source = (cells.unit_price ?? cells.item_description ?? Object.values(cells)[0])?.source_row;
    if (!source) return [];
    return documentRows(documents, PRICE_TIERS)
        .filter(({row}) => `${String(row.document_id)}:${String(row.item_line_no)}` === source);
}

/** The basis of a version's end, in words. */
export function endBasisLabel(basis: string): string {
    if (basis === 'open') return 'No end stated';
    if (basis === 'next version') return 'Until the next version';
    if (basis === 'termination') return 'Ended by a termination';
    return `Stated (${typeLabel(basis).toLowerCase()})`;
}

type Figure = {label: string; value: number};

/** What a finding is worth: its estimated saving for a leakage, the amount at risk for a risk. */
export function opportunityFigure(o: Pick<ContractOpportunity, 'category' | 'estimated_saving' | 'amount_at_risk'>): Figure {
    return o.category === 'leakage'
        ? {label: 'Estimated saving', value: o.estimated_saving ?? 0}
        : {label: 'At risk', value: o.amount_at_risk};
}

/** The bundle's totals over every kind: findings, estimated saving, amount at risk of the risks only. */
export function opportunityTotals(counts: OpportunityCount[]): {findings: number; saving: number; atRisk: number} {
    return counts.reduce((total, c) => ({
        findings: total.findings + c.findings,
        saving: total.saving + c.estimated_saving,
        atRisk: total.atRisk + (KIND_CATEGORY[c.kind] === 'risk' ? c.amount_at_risk : 0),
    }), {findings: 0, saving: 0, atRisk: 0});
}

/** One column of a finding's transactions: a fact of its rows, or a composite of several (`link`, `category`). */
export type EvidenceColumn = {key: string; label: string; numeric?: boolean};

const QUANTITY = {key: 'quantity', label: 'Quantity', numeric: true};
const BILLED = {key: 'billed_unit_price', label: 'Billed unit price', numeric: true};
const LINE_PRICE = {key: 'line_price', label: 'Line price', numeric: true};
const OVERBILLED = {key: 'overbilled_per_unit', label: 'Overbilled per unit', numeric: true};
const LINK = {key: 'link', label: 'Line · version'};
const ENTITY = {key: 'entity', label: 'Entity'};
const SUPPLIER = {key: 'supplier_name', label: 'Supplier'};

/** What each kind's transactions are measured on, in order: the inputs its saving was computed from first. */
export const KIND_EVIDENCE_COLUMNS: Record<OpportunityKind, EvidenceColumn[]> = {
    price_above_contract: [QUANTITY, BILLED, LINE_PRICE, OVERBILLED, LINK, ENTITY],
    amendment_price_not_applied: [QUANTITY, BILLED, LINE_PRICE, OVERBILLED, LINK, ENTITY],
    tier_not_applied: [QUANTITY, BILLED, LINE_PRICE, {key: 'band_price', label: 'Band price', numeric: true}, LINK, ENTITY],
    shorter_payment_terms: [{key: 'terms', label: 'Terms'}, {key: 'invoice_days', label: 'Invoice days', numeric: true},
        {key: 'contract_days', label: 'Contract days', numeric: true}, {key: 'days_early', label: 'Days early', numeric: true},
        ENTITY],
    duplicate_invoice: [{key: 'invoice', label: 'Invoice'}, {key: 'copies', label: 'Copies', numeric: true}, SUPPLIER, ENTITY],
    outside_period: [ENTITY, LINK],
    off_contract_supplier: [SUPPLIER, ENTITY],
    maverick: [SUPPLIER, {key: 'category', label: 'Category'}, ENTITY],
};

// The facts a composite column shows, and the ones the grid's fixed columns already show.
const COMPOSED: Record<string, string[]> = {
    link: ['link_line', 'link_version', 'link_confidence'],
    category: ['level_1', 'level_2', 'level_3'],
};
const ALWAYS = ['date'];

/** The kind's columns, and the facts its rows hold that no column shows, for an "Other facts" column. */
export function evidenceColumns(kind: OpportunityKind, rows: {facts: Record<string, unknown>}[]):
{columns: EvidenceColumn[]; others: string[]} {
    const columns = KIND_EVIDENCE_COLUMNS[kind] ?? [];
    const shown = new Set([...ALWAYS, ...columns.flatMap(c => COMPOSED[c.key] ?? [c.key])]);
    const others = [...new Set(rows.flatMap(r => Object.keys(r.facts ?? {})))].filter(k => !shown.has(k));
    return {columns, others};
}

/** A column's value in a row: the line and version it is linked to (`L1 · v2 · 0.80`), its finest category, or the fact. */
export function evidenceValue(key: string, facts: Record<string, unknown>): unknown {
    if (key === 'link') {
        if (facts.link_version === undefined || facts.link_version === null) return null;
        const confidence = typeof facts.link_confidence === 'number' ? ` · ${facts.link_confidence.toFixed(2)}` : '';
        return `${facts.link_line || 'whole'} · v${facts.link_version}${confidence}`;
    }
    if (key === 'category') return facts.level_3 || facts.level_2 || facts.level_1 || null;
    return facts[key];
}
